import { Box, Text } from "@chakra-ui/react";
import { useField, useFormikContext } from "formik";
import { FC, useCallback, useMemo } from "react";

import { Select } from "~/src/components/common/Select";

import {
  findFunnelOptionByImportedId,
  findLandingPageForFunnel,
  funnelPageUrlsContain,
  pageImportPickerValue,
} from "../popupImportFunnelOptions";
import { usePopupImportFunnelOptions } from "../PopupImportFunnelOptionsContext";
import { usePopupImportResolution } from "../PopupImportResolutionContext";
import { popupImportSelectWithMenuPortalProps } from "../popupImportSelectMenuPortal";
import type { PopupImportFormValues } from "../validationSchema";

const EMPTY_FUNNEL = { label: "—", value: "" };

export const PopupImportFunnelSelectCell: FC<{
  name: string;
  popupIndex: number;
}> = ({ name, popupIndex }) => {
  const { funnelOptions } = usePopupImportFunnelOptions();
  const { getMessage, clearMessage } = usePopupImportResolution();
  const { values, setFieldValue } = useFormikContext<PopupImportFormValues>();
  const [{ value, onBlur }, { touched, error }, { setValue }] =
    useField<string>(name);

  const funnelNamePath = `popups.${popupIndex}.funnelName`;
  const pageUrlPath = `popups.${popupIndex}.verificationPageUrl`;
  const pageIdPath = `popups.${popupIndex}.verificationPageId`;
  const currentPageUrl = values.popups[popupIndex]?.verificationPageUrl ?? "";

  const options = useMemo(
    () => [
      EMPTY_FUNNEL,
      ...funnelOptions.map((f) => ({ label: f.name, value: f.id })),
    ],
    [funnelOptions]
  );

  const calculatedValue = useMemo(() => {
    const v = (value ?? "").trim();
    const matched = findFunnelOptionByImportedId(v, funnelOptions);
    if (matched) {
      return options.find((o) => o.value === matched.id);
    }
    return options.find((o) => o.value === v);
  }, [options, value, funnelOptions]);

  const resolutionMsg = getMessage(popupIndex, "funnel");
  const showFieldError = touched && !!error;

  const handleChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (opt: any) => {
      const v = (opt?.value as string | undefined) ?? "";
      void setValue(v);
      const funnel = funnelOptions.find((f) => f.id === v);
      void setFieldValue(funnelNamePath, funnel?.name ?? "");
      clearMessage(popupIndex, "funnel");

      const p = (currentPageUrl ?? "").trim();
      if (!v) {
        if (p) void setFieldValue(pageUrlPath, "");
        void setFieldValue(pageIdPath, "");
        clearMessage(popupIndex, "page");
        return;
      }

      const keepExistingPage =
        !!p && funnelPageUrlsContain(funnelOptions, v, p);
      if (keepExistingPage) {
        clearMessage(popupIndex, "page");
        return;
      }

      const landing = findLandingPageForFunnel(funnel);
      if (landing) {
        void setFieldValue(pageIdPath, pageImportPickerValue(landing));
        void setFieldValue(pageUrlPath, landing.url);
        clearMessage(popupIndex, "page");
      } else {
        void setFieldValue(pageUrlPath, "");
        void setFieldValue(pageIdPath, "");
        clearMessage(popupIndex, "page");
      }
    },
    [
      setValue,
      funnelOptions,
      funnelNamePath,
      pageUrlPath,
      pageIdPath,
      setFieldValue,
      clearMessage,
      popupIndex,
      currentPageUrl,
    ]
  );

  return (
    <Box>
      <Select
        name={name}
        options={options}
        value={calculatedValue}
        error={showFieldError ? error : undefined}
        onBlur={onBlur}
        onChange={handleChange}
        isSearchable
        {...popupImportSelectWithMenuPortalProps}
      />
      {resolutionMsg ? (
        <Text fontSize="11px" color="red.500" mt="4px">
          {resolutionMsg}
        </Text>
      ) : null}
    </Box>
  );
};

export const PopupImportFunnelPageSelectCell: FC<{
  name: string;
  popupIndex: number;
}> = ({ name, popupIndex }) => {
  const { funnelOptions } = usePopupImportFunnelOptions();
  const { getMessage, clearMessage } = usePopupImportResolution();
  const { values, setFieldValue } = useFormikContext<PopupImportFormValues>();
  const [{ value, onBlur }, { touched, error }, { setValue }] =
    useField<string>(name);

  const funnelIdRaw = values.popups[popupIndex]?.verificationFunnelId ?? "";
  const funnel = findFunnelOptionByImportedId(funnelIdRaw, funnelOptions);
  const funnelId = funnel?.id ?? "";

  const options = useMemo(() => {
    const pages = funnel?.pages ?? [];
    return [
      { label: "—", value: "" },
      ...pages.map((p) => ({
        label: p.name || p.url,
        value: pageImportPickerValue(p),
      })),
    ];
  }, [funnel]);

  const calculatedValue = useMemo(() => {
    const v = value ?? "";
    const emptyOpt = options.find((o) => o.value === "") ?? options[0];
    if (!v.trim()) return emptyOpt;
    const exact = options.find((o) => o.value === v.trim());
    if (exact) return exact;
    if (!funnel) return emptyOpt;
    const page = funnel.pages.find(
      (p) =>
        pageImportPickerValue(p) === v.trim() ||
        (p.encryptedId ?? "").trim() === v.trim()
    );
    if (!page) return emptyOpt;
    return (
      options.find((o) => o.value === pageImportPickerValue(page)) ?? emptyOpt
    );
  }, [options, value, funnel]);

  const resolutionMsg = getMessage(popupIndex, "page");
  const showFieldError = touched && !!error;

  const pageUrlPath = `popups.${popupIndex}.verificationPageUrl`;

  const handleChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (opt: any) => {
      const id = (opt?.value as string | undefined) ?? "";
      void setValue(id);
      const page = id
        ? funnel?.pages.find((p) => pageImportPickerValue(p) === id)
        : undefined;
      void setFieldValue(pageUrlPath, page?.url ?? "");
      clearMessage(popupIndex, "page");
    },
    [setValue, setFieldValue, pageUrlPath, funnel, clearMessage, popupIndex]
  );

  const isDisabled = !funnelId;

  return (
    <Box>
      <Select
        key={`verification-page-${popupIndex}-${(value ?? "").trim()}`}
        name={name}
        options={options}
        value={calculatedValue}
        error={showFieldError ? error : undefined}
        onBlur={onBlur}
        onChange={handleChange}
        isDisabled={isDisabled}
        isSearchable
        {...popupImportSelectWithMenuPortalProps}
      />
      {resolutionMsg ? (
        <Text fontSize="11px" color="red.500" mt="4px">
          {resolutionMsg}
        </Text>
      ) : null}
    </Box>
  );
};
